var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { fmtNum } from '../format';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { DataGridWindow } from './data-grid-window';
/*
 * <vitesse-data-grid> — a windowed, bidirectionally-paged virtual record grid: the
 * DataGrip "data editor" surface for an integration table, correct at ANY size
 * (1k rows or 1 billion).
 *
 * TWO independent bounds keep it flat regardless of table size:
 *   • DOM virtualization — only the ~viewport rows are in the DOM (fixed row height
 *     → a render window over the buffer), so 1B rows cost the same DOM as 50.
 *   • A VIEWPORT-SIZED SLIDING WINDOW of pages — owned by DataGridWindow (see
 *     data-grid-window.ts), the extracted, Node-testable paging engine: it holds
 *     ONLY what you're looking at + buffer (cap derived from the measured
 *     viewport), replays dropped pages by bookmark when you scroll back up (14 of
 *     15 drivers have no reverse cursors), trims with hysteresis so boundary
 *     wiggles never refetch, and retries failures with backoff + a hang watchdog
 *     so the grid can never be silently, permanently stuck.
 *
 * This element keeps only what needs a browser: rendering, viewport measurement,
 * and scrollTop compensation when the engine shifts the window's top edge.
 *
 * Honest infinite scroll: the scrollbar tracks the LOADED window, not a fake
 * billion-row height (a real one is impossible past the browser's ~33M px element
 * cap anyway, and pixel-targeting row 487,000,000 by drag is meaningless). With
 * keyset cursors every page — forward or backward, shallow or deep — is O(log n).
 *
 * Shadow DOM + token CSS vars (they pierce the boundary) → drops into any Vitesse
 * product UI and themes itself. Lit-only, no virtualization dependency.
 */
let VitesseDataGrid = class VitesseDataGrid extends LitElement {
    constructor() {
        super();
        /** Column order + labels (canonical — from the table's schema, so order is stable
         *  even though row cells arrive as an unordered map). */
        this.columns = [];
        /** Reset key — when it changes (e.g. a new table selected) the window is cleared
         *  and reloaded from the first page. */
        this.resetKey = '';
        /** Fixed row height in px — the virtualization unit. */
        this.rowHeight = 28;
        /** Extra rows beyond the visible viewport to fetch per page + keep buffered each
         *  side (the smoothness knob). The page size + window cap derive from this + the
         *  measured viewport — NOT fixed large numbers — so the buffer is "what you see + a
         *  screen each way", a couple-hundred rows at most, the rest fetched on scroll. */
        this.overscan = 12;
        /** When set, rows become SELECTABLE: this cell key is the row's identity. Clicking a
         *  row emits `row-click` ({key,row}); the row whose key === selectedKey is highlighted.
         *  Lets the same windowed grid serve as a navigator (e.g. a paged table list). */
        this.rowKey = '';
        this.selectedKey = '';
        /* ── in-cell editing (opt-in; the grid is read-only until all three are set) ──
         *
         * The element owns the INTERACTION — where the box appears, Enter/Escape, the
         * pending and reverted states — and the host owns the IO, injected as
         * `commitCell`. That is the same boundary `source` keeps: this file never
         * imports a transport and never learns what a write costs.
         */
        /** Column keys whose cells may be edited. Empty (the default) = read-only. */
        this.editableColumns = [];
        this.scrollPos = 0;
        this.viewportH = 0;
        /** The cell currently being typed into (null = none). Keyed by row IDENTITY. */
        this.editing = null;
        /** Cells whose write is in flight, as `<id>\u0000<column>` (cellSlot) — they render dimmed and
         *  refuse a second edit until they settle. */
        this.pending = new Set();
        /** Optimistic values painted before the write settled, so the reader sees the
         *  edit immediately and sees it REVERT if the source refuses. */
        this.overlay = new Map();
        this.rows = []; // flattened window (mirrors the engine)
        this.rafPending = false;
        this.quietUntil = 0; // suppress SCROLL-driven fetches briefly after a compensation
        this.onEditKey = (e, row, column) => {
            // Enter commits, Escape abandons. Blur abandons too (see below) — the
            // conservative reading, opposite to the description field's forgiving one,
            // because what is on the other end of this box is a customer's database.
            if (e.key === 'Enter') {
                e.preventDefault();
                const raw = e.target.value;
                void this.commitEdit(row, column, raw);
            }
            else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                this.cancelEdit();
            }
        };
        this.onScroll = (e) => {
            const el = e.currentTarget;
            if (this.rafPending)
                return;
            this.rafPending = true;
            requestAnimationFrame(() => {
                this.rafPending = false;
                this.scrollPos = el.scrollTop;
                if (this.viewportH === 0)
                    this.viewportH = el.clientHeight;
                this.planFetch(false);
            });
        };
        // Prolonged dwell near the bottom edge preloads the next page before the scroll
        // reaches it (the lazy "hover to preload").
        this.onPointerMove = (e) => {
            if (this.engine.status !== '' || this.engine.atEnd || !this.scrollEl)
                return;
            const band = this.scrollEl.getBoundingClientRect();
            const nearBottom = e.clientY > band.bottom - Math.max(60, band.height * 0.2);
            if (this.dwellTimer)
                clearTimeout(this.dwellTimer);
            if (nearBottom)
                this.dwellTimer = window.setTimeout(() => this.planFetch(false), 80);
        };
        this.onPointerLeave = () => {
            if (this.dwellTimer)
                clearTimeout(this.dwellTimer);
        };
        new LocaleController(this); // footer strings re-render on language change
        this.engine = new DataGridWindow({
            pageSize: () => this.pageSize(),
            windowCap: () => this.windowCap(),
            onChange: (fx) => this.onEngineChange(fx),
        });
    }
    static { this.styles = css `
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      overflow: hidden;
      font-size: 13px;
      color: var(--text-primary);
    }
    .head {
      display: flex;
      flex: none;
      border-bottom: 1px solid var(--border-color);
      background: var(--bg-secondary, var(--bg-primary));
    }
    .hcell,
    .cell {
      flex: 1 1 0;
      min-width: 0;
      padding: 0 12px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      box-sizing: border-box;
    }
    .hcell {
      line-height: 30px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: var(--text-secondary);
      user-select: none;
    }
    .hcell.num,
    .cell.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    .scroll {
      position: relative;
      flex: 1;
      min-height: 0;
      overflow: auto;
    }
    .spacer {
      position: relative;
      width: 100%;
    }
    .row {
      position: absolute;
      left: 0;
      right: 0;
      display: flex;
      align-items: center;
      border-bottom: 1px solid var(--border-color-light, var(--border-color));
    }
    .row:hover {
      background: var(--hover-bg);
    }
    .row.selectable {
      cursor: pointer;
    }
    .row.sel {
      background: var(--hover-bg);
      box-shadow: inset 2px 0 0 var(--blue);
    }
    .cell {
      line-height: 1.3;
    }
    .cell.null {
      color: var(--text-tertiary, var(--sub-text));
      font-style: italic;
    }
    .cell.bool {
      color: var(--blue);
    }
    /* An editable cell says so on hover and nowhere else: a grid whose every cell
       carried an affordance at rest would read as a form, which is the thing this
       is meant not to be. */
    .cell.editable:hover {
      background: var(--hover-bg);
      box-shadow: inset 0 0 0 1px var(--border-color);
      border-radius: 3px;
      cursor: text;
    }
    .cell.pending {
      opacity: 0.55;
      font-style: italic;
    }
    .cell.editing {
      padding: 0 6px;
      display: flex;
      align-items: center;
    }
    .cell-input {
      width: 100%;
      min-width: 0;
      font: inherit;
      color: var(--text-primary);
      background: var(--bg-primary);
      border: 1px solid var(--blue);
      border-radius: 3px;
      padding: 1px 5px;
      box-sizing: border-box;
      outline: none;
    }
    .cell.num .cell-input {
      text-align: right;
    }
    .footer {
      flex: none;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 11px;
      color: var(--text-secondary);
      border-top: 1px solid var(--border-color);
    }
    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--blue);
      animation: pulse 1s ease-in-out infinite;
    }
    .dot.warn {
      background: var(--orange, var(--yellow, var(--blue)));
    }
    @keyframes pulse {
      0%,
      100% {
        opacity: 0.3;
      }
      50% {
        opacity: 1;
      }
    }
    .empty {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--text-secondary);
      font-size: 13px;
    }
  `; }
    connectedCallback() {
        super.connectedCallback();
        this.ro = new ResizeObserver(() => {
            if (this.scrollEl)
                this.viewportH = this.scrollEl.clientHeight;
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.ro?.disconnect();
        if (this.dwellTimer)
            clearTimeout(this.dwellTimer);
        this.engine.dispose();
    }
    willUpdate(changed) {
        // Reset + reload whenever the source or the reset key changes (new table).
        if (changed.has('source') || changed.has('resetKey')) {
            this.rows = [];
            this.scrollPos = 0;
            if (this.scrollEl)
                this.scrollEl.scrollTop = 0;
            // A new table (or filter) means the rows behind an open editor are gone and
            // the optimistic paint no longer describes anything on screen. Abandon both,
            // or a commit could land on whichever row now sits where the old one did.
            this.editing = null;
            if (this.overlay.size)
                this.overlay = new Map();
            if (this.source)
                this.engine.attach(this.source);
            else
                this.engine.dispose();
        }
    }
    // ── in-cell editing ────────────────────────────────────────────────────────
    /** Editing is live only when the host supplied all three parts. */
    get canEdit() {
        return !!this.commitCell && !!this.rowId && this.editableColumns.length > 0;
    }
    cellSlot(id, column) {
        // NUL-separated: no column name can contain it, so the slot key cannot collide.
        return `${id}\u0000${column}`;
    }
    /** The value to SHOW: the optimistic paint if one is outstanding, else the row's
     *  own cell. */
    shown(row, column) {
        if (this.overlay.size && this.rowId) {
            const slot = this.cellSlot(this.rowId(row), column);
            if (this.overlay.has(slot))
                return this.overlay.get(slot);
        }
        return row[column];
    }
    beginEdit(row, column) {
        if (!this.canEdit || !this.rowId)
            return;
        if (!this.editableColumns.includes(column))
            return;
        const id = this.rowId(row);
        if (this.pending.has(this.cellSlot(id, column)))
            return; // a write is still in flight
        this.editing = { id, column };
    }
    cancelEdit() {
        this.editing = null;
    }
    /** Commit what was typed. The value is painted immediately and REVERTS if the
     *  source refuses — the same optimistic-with-rollback shape the symbol grid uses
     *  for a padlock, and for the same reason: an optimism that survives a refusal
     *  shows a change that was never made. */
    async commitEdit(row, column, next) {
        const commit = this.commitCell;
        if (!commit || !this.rowId)
            return;
        const id = this.rowId(row);
        const slot = this.cellSlot(id, column);
        this.editing = null;
        this.overlay = new Map(this.overlay).set(slot, next);
        this.pending = new Set(this.pending).add(slot);
        try {
            // Write the stored value into the buffer row, so the row now describes what
            // the source holds. Editing the same cell again therefore builds on the new
            // value, not the one it had before this edit.
            row[column] = await commit(row, column, next);
        }
        catch {
            // Refused — nothing to write back; dropping the overlay below restores what
            // was there. The reason is the caller's to surface: it owns the source's
            // prose and the toast.
        }
        finally {
            // The overlay is scaffolding for the in-flight moment only. Either the row
            // now carries the new value or it still carries the old one; in both cases
            // the row itself is the truth to paint.
            const back = new Map(this.overlay);
            back.delete(slot);
            this.overlay = back;
            const done = new Set(this.pending);
            done.delete(slot);
            this.pending = done;
        }
    }
    firstUpdated() {
        this.acquireScroll();
    }
    updated() {
        this.acquireScroll();
        // Focus the editor the moment it appears. `autofocus` is unreliable for an
        // element created inside a shadow root after first paint, and an edit box that
        // does not take the keystroke it was opened for is worse than none.
        if (this.editing) {
            const input = this.renderRoot.querySelector('.cell-input');
            if (input && this.renderRoot.activeElement !== input) {
                input.focus();
                input.select();
            }
        }
    }
    acquireScroll() {
        const sc = this.renderRoot.querySelector('.scroll');
        if (!sc || sc === this.scrollEl)
            return;
        this.scrollEl = sc;
        this.viewportH = sc.clientHeight;
        this.ro?.observe(sc);
    }
    // ── viewport-relative sizing: the buffer is "what you see + a screen each way",
    //    NOT a fixed large cap. Everything derives from the measured viewport. ──
    /** Rows that fit in the viewport right now. */
    get visibleRows() {
        return Math.max(1, Math.ceil((this.viewportH || 400) / this.rowHeight));
    }
    /** Page size = one screen + overscan (so a page ≈ one viewport). */
    pageSize() {
        return this.visibleRows + this.overscan;
    }
    /** Window cap = a few screens (viewport + ~one page of buffer each side). Far pages
     *  drop past this (with hysteresis — see the engine), so memory is a
     *  couple-hundred rows regardless of table size. */
    windowCap() {
        return this.pageSize() * 3;
    }
    /** Fetch when within ~half a screen of an edge. */
    get prefetchRows() {
        return Math.ceil(this.visibleRows / 2) + 4;
    }
    /** Engine state changed: mirror the rows, then — after the DOM catches up —
     *  compensate scrollTop for any top-edge shift (prepend pushes rows down,
     *  a top drop pulls them up) so the viewport never visually jumps, and
     *  re-plan with the POST-shift row indices (a deep fling can still be inside
     *  the prefetch band after a landing — don't wait for the next scroll event). */
    onEngineChange(fx) {
        this.rows = this.engine.rows;
        this.requestUpdate();
        const shift = fx.prepended - fx.droppedTop;
        void this.updateComplete.then(() => {
            if (shift !== 0 && this.scrollEl) {
                // The compensation below fires a synthetic scroll event that sits near
                // the OPPOSITE edge; without a cooldown it would trigger a bogus fetch.
                this.quietUntil = performance.now() + 90;
                this.scrollEl.scrollTop = Math.max(0, this.scrollEl.scrollTop + shift * this.rowHeight);
                this.scrollPos = this.scrollEl.scrollTop;
            }
            this.planFetch(true);
        });
    }
    /** Report the visible row range to the engine, which decides whether to fetch.
     *  `trusted` bypasses the post-compensation cooldown: it is set only when WE
     *  just recomputed scrollPos ourselves, so the indices are genuine — the
     *  cooldown exists to ignore the browser's synthetic scroll echo, not this. */
    planFetch(trusted) {
        if (!trusted && performance.now() < this.quietUntil)
            return;
        const firstVisible = Math.floor(this.scrollPos / this.rowHeight);
        const lastVisible = Math.ceil((this.scrollPos + this.viewportH) / this.rowHeight);
        this.engine.ensure(firstVisible, lastVisible, this.prefetchRows);
    }
    isNumeric(type) {
        if (!type)
            return false;
        return /int|float|double|real|numeric|decimal|serial|money|number/.test(type.toLowerCase());
    }
    renderCell(row, col) {
        const v = this.shown(row, col.key);
        const num = this.isNumeric(col.type);
        const id = this.canEdit && this.rowId ? this.rowId(row) : '';
        const editable = this.canEdit && this.editableColumns.includes(col.key);
        if (editable && this.editing?.id === id && this.editing.column === col.key) {
            return html `<div class="cell editing ${num ? 'num' : ''}">
        <input
          class="cell-input"
          .value=${v === null || v === undefined ? '' : String(v)}
          @keydown=${(e) => this.onEditKey(e, row, col.key)}
          @blur=${() => this.cancelEdit()}
          @focus=${(e) => e.target.select()}
          autofocus
        />
      </div>`;
        }
        const pending = editable && this.pending.has(this.cellSlot(id, col.key));
        const cls = `cell${editable ? ' editable' : ''}${pending ? ' pending' : ''}${num ? ' num' : ''}`;
        const open = editable ? () => this.beginEdit(row, col.key) : undefined;
        if (v === null || v === undefined) {
            return html `<div class="${cls} null" @dblclick=${open}>∅</div>`;
        }
        if (typeof v === 'boolean') {
            return html `<div class="${cls} bool" @dblclick=${open}>${v ? 'true' : 'false'}</div>`;
        }
        return html `<div class="${cls}" title=${String(v)} @dblclick=${open}>${String(v)}</div>`;
    }
    render() {
        const total = this.rows.length;
        const cols = this.columns;
        const buffer = 8;
        const first = Math.max(0, Math.floor(this.scrollPos / this.rowHeight) - buffer);
        const visible = Math.ceil((this.viewportH || 400) / this.rowHeight) + buffer * 2;
        const last = Math.min(total, first + visible);
        const selectable = this.rowKey !== '';
        const window = [];
        for (let i = first; i < last; i++) {
            const row = this.rows[i];
            const key = selectable ? String(row[this.rowKey] ?? '') : '';
            const sel = selectable && key === this.selectedKey;
            window.push(html `
        <div
          class="row${selectable ? ' selectable' : ''}${sel ? ' sel' : ''}"
          style="top:${i * this.rowHeight}px;height:${this.rowHeight}px"
          @click=${selectable ? () => this.dispatchEvent(new CustomEvent('row-click', { detail: { key, row }, bubbles: true, composed: true })) : undefined}
        >
          ${cols.map((c) => this.renderCell(row, c))}
        </div>
      `);
        }
        const status = this.engine.status;
        const more = !this.engine.atEnd || !this.engine.atStart;
        return html `
      <div class="head">
        ${cols.map((c) => html `<div class="hcell ${this.isNumeric(c.type) ? 'num' : ''}" title=${c.label}>${c.label}</div>`)}
      </div>
      ${total === 0 && !status
            ? html `<div class="empty">${t('dataGrid.noRows')}</div>`
            : html `
              <div class="scroll" @scroll=${this.onScroll} @pointermove=${this.onPointerMove} @pointerleave=${this.onPointerLeave}>
                <div class="spacer" style="height:${total * this.rowHeight}px">${window}</div>
              </div>
            `}
      ${status || more
            ? html `<div class="footer">
              ${status === 'retrying'
                ? html `<span class="dot warn"></span> ${t('dataGrid.retrying')}`
                : status === 'loading'
                    ? html `<span class="dot"></span> ${t('dataGrid.loading')}`
                    : html `<span>${t('dataGrid.loadedMore', { n: fmtNum(total) })}</span>`}
            </div>`
            : total > 0
                ? html `<div class="footer">${t('dataGrid.rows', { n: fmtNum(total) })}</div>`
                : nothing}
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseDataGrid.prototype, "columns", void 0);
__decorate([
    property({ attribute: false })
], VitesseDataGrid.prototype, "source", void 0);
__decorate([
    property()
], VitesseDataGrid.prototype, "resetKey", void 0);
__decorate([
    property({ type: Number })
], VitesseDataGrid.prototype, "rowHeight", void 0);
__decorate([
    property({ type: Number })
], VitesseDataGrid.prototype, "overscan", void 0);
__decorate([
    property()
], VitesseDataGrid.prototype, "rowKey", void 0);
__decorate([
    property()
], VitesseDataGrid.prototype, "selectedKey", void 0);
__decorate([
    property({ attribute: false })
], VitesseDataGrid.prototype, "editableColumns", void 0);
__decorate([
    property({ attribute: false })
], VitesseDataGrid.prototype, "rowId", void 0);
__decorate([
    property({ attribute: false })
], VitesseDataGrid.prototype, "commitCell", void 0);
__decorate([
    state()
], VitesseDataGrid.prototype, "scrollPos", void 0);
__decorate([
    state()
], VitesseDataGrid.prototype, "viewportH", void 0);
__decorate([
    state()
], VitesseDataGrid.prototype, "editing", void 0);
__decorate([
    state()
], VitesseDataGrid.prototype, "pending", void 0);
__decorate([
    state()
], VitesseDataGrid.prototype, "overlay", void 0);
VitesseDataGrid = __decorate([
    customElement('vitesse-data-grid')
], VitesseDataGrid);
export { VitesseDataGrid };
//# sourceMappingURL=vitesse-data-grid.js.map